/**
 * 添加/编辑密码条目表单
 * 支持自动分类、生成随机密码、本地加密后上传
 */

import React, { useState } from "react";
import { useStore } from "../store";
import { createItem, updateItem } from "../api";
import {
  classifyUrl,
  extractDomain,
  makeTitleFromUrl,
  getFavicon,
  passwordStrength,
  generatePassword,
  encrypt,
  CATEGORY_NAMES,
} from "@passkeeper/shared";
import type { DecryptedItem, PasswordCategory } from "@passkeeper/shared";

interface Props {
  editItem?: DecryptedItem | null;
  showToast: (msg: string, type: "success" | "error" | "info") => void;
  onClose: () => void;
  onSaved: () => void;
}

const CATEGORIES: PasswordCategory[] = [
  "social", "email", "shopping", "finance",
  "work", "entertainment", "developer", "ai", "other",
];

export default function AddItem({ editItem, showToast, onClose, onSaved }: Props) {
  const masterPassword = useStore((s) => s.masterPassword);
  const [loading, setLoading] = useState(false);

  const [url, setUrl] = useState(editItem?.url || "");
  const [title, setTitle] = useState(editItem?.title || "");
  const [username, setUsername] = useState(editItem?.username || "");
  const [password, setPassword] = useState(editItem?.password || "");
  const [notes, setNotes] = useState(editItem?.notes || "");
  const [category, setCategory] = useState<PasswordCategory>(
    editItem?.category || "other"
  );
  const [showPw, setShowPw] = useState(false);
  const [autoCategory, setAutoCategory] = useState(true);

  // URL 变更时自动识别分类和标题
  const handleUrlChange = (val: string) => {
    setUrl(val);
    if (autoCategory && val) {
      setCategory(classifyUrl(val));
    }
    if (!editItem && val) {
      setTitle(makeTitleFromUrl(val));
    }
  };

  const handleGeneratePassword = () => {
    const newPw = generatePassword(16);
    setPassword(newPw);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!masterPassword) {
      showToast("未设置主密码，无法加密", "error");
      return;
    }

    if (!title) {
      showToast("请填写站点名称", "error");
      return;
    }

    setLoading(true);

    try {
      // 本地加密 username + password + notes 整体
      const plaintext = JSON.stringify({ username, password, notes });
      const { cipher, iv, salt } = await encrypt(masterPassword, plaintext);

      const payload = {
        url,
        title,
        category,
        encData: cipher,
        iv,
        salt,
        // 备用明文（侧边显示）
        username,
        password,
        notes,
      };

      let result;
      if (editItem) {
        result = await updateItem(editItem.id, payload);
      } else {
        result = await createItem(payload);
      }

      if (result.ok) {
        showToast(editItem ? "更新成功" : "添加成功", "success");
        onSaved();
      } else {
        showToast(result.error || "保存失败", "error");
      }
    } catch (err) {
      showToast("操作失败: " + (err instanceof Error ? err.message : "未知错误"), "error");
    }

    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editItem ? "编辑密码" : "添加密码"}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* 站点 URL */}
            <div className="form-group">
              <label>站点 URL</label>
              <input
                type="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
              />
            </div>

            {/* 站点名称 */}
            <div className="form-group">
              <label>站点名称</label>
              <input
                type="text"
                placeholder="如：Gmail、GitHub"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            {/* 账号 */}
            <div className="form-group">
              <label>账号</label>
              <input
                type="text"
                placeholder="邮箱或用户名"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            {/* 密码 */}
            <div className="form-group">
              <label>密码</label>
              <div className="password-field">
                <input
                  type={showPw ? "text" : "password"}
                  placeholder="输入或生成密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPw(!showPw)}
                >
                  {showPw ? "🙈" : "👁"}
                </button>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={handleGeneratePassword}
                >
                  🎲 生成密码
                </button>
                {password && (
                  <span style={{ fontSize: "0.75rem", color: "#64748b", alignSelf: "center" }}>
                    强度: {"⭐".repeat(passwordStrength(password))}
                  </span>
                )}
              </div>
            </div>

            {/* 备注 */}
            <div className="form-group">
              <label>备注</label>
              <textarea
                rows={2}
                placeholder="可选备注信息"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ resize: "vertical" }}
              />
            </div>

            {/* 分类 */}
            <div className="form-group">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <label style={{ marginBottom: 0 }}>分类</label>
                <label style={{ fontSize: "0.75rem", color: "#64748b", display: "flex", alignItems: "center", gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={autoCategory}
                    onChange={(e) => setAutoCategory(e.target.checked)}
                  />
                  自动识别
                </label>
              </div>
              <select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value as PasswordCategory);
                  setAutoCategory(false);
                }}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_NAMES[cat]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              取消
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "保存中..." : editItem ? "保存修改" : "添加"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}